<template>
  <v-card flat :color="$vuetify.theme.dark ? '#1E1E1E' : '#F5F5F5'" class="pa-2 card-class-container">

    <!-- 수강생 정보 및 화상통화 앱 선택 섹션 -->
    <v-row dense class="mt-1">
      <v-col cols="12">
        <v-card class="section-card" outlined>
          <v-card-title class="section-title py-1">
      
            <slot name="title"></slot>   
            <v-row class="g-0">
              <v-col cols="12">
                <v-icon left color="primary" small>mdi-account</v-icon>
                <span class="title-text">{{ $t('student_info') }}</span>
              </v-col>
            </v-row>
          </v-card-title>
          <v-card-text class="pa-2">
            <v-row dense>
              <v-col cols="12" sm="6">
                <v-text-field
                  :label="$t('english_name')"
                  :value="studentEnglishName"
                  outlined
                  dense
                  hide-details
                  class="rounded-lg mb-2"
                  @keyup.enter="saveEnglishName"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  ref="skypeId"
                  :label="`${selectedAssign.callAppType} Id`"
                  v-model="selectedAssign.skypeId"
                  :placeholder="studentEmail"
                  :rules="skypeIdRule"
                  outlined
                  dense
                  class="rounded-lg"
                  :hint="$t('enter_account', { app: selectedAssign.callAppType })"
                  persistent-hint
                  required
                ></v-text-field>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12"  >
        <v-card class="section-card" outlined>
          <v-card-title class="section-title py-2">
            <v-icon left color="primary" small>mdi-video</v-icon>
            <span class="title-text">{{ $t('select_call_app') }}</span>
          </v-card-title>
          <v-card-text class="pa-2">
            <v-item-group v-model="selectedAssign.callAppType" mandatory>
              <v-row dense>
                <v-col v-for="item in app.callAppType" :key="item.appCode" cols="6">
                  <v-item v-slot="{ active, toggle }" :value="item.appCode">
                    <v-btn
                      block
                      :color="active ? 'primary' : ''"
                      :class="{'white--text': active, 'app-select-btn': true}"
                      @click="toggle(); selectApp(item)"
                      class="justify-start"
                      height="40"
                      elevation="1"
                      x-small
                    >
                      <v-img :src="callAppImg(item.appCode)" max-width="20" max-height="20" class="mr-2"></v-img>
                      {{ item.appName }}
                    </v-btn>
                  </v-item>
                </v-col>
              </v-row>
            </v-item-group>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- 수업 일정 섹션 -->
    <v-card class="section-card mt-2" outlined>
      <v-card-title class="section-title py-2">
        <v-icon left color="primary" small>mdi-calendar</v-icon>
        <span class="title-text">{{ $t('class_schedule') }}</span>
      </v-card-title>
      <v-card-text class="pa-2">
        <v-row dense>
          <v-col cols="12" sm="6" md="3">
            <v-menu v-model="datePickerMenu" :close-on-content-click="false"
                  transition="scale-transition" offset-y min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="selectedAssign.startDate"
                  :label="$t('class_start_date')"
                  readonly
                  outlined
                  dense
                  hide-details
                  class="rounded-lg mb-2"
                  v-bind="attrs"
                  v-on="on"
                  prepend-inner-icon="mdi-calendar"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="selectedAssign.startDate"
                @input="datePickerMenu = false"
                scrollable
                color="primary"
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-dialog
              ref="dialog"
              v-model="timePickerMenu"
              :model-value="selectedAssign.classStartTime"
              @update:model-value="val => selectedAssign.classStartTime = val"
              persistent
              width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  :value="formattedTime"
                  :label="$t('class_start_time')"
                  readonly
                  outlined
                  dense
                  hide-details
                  class="rounded-lg"
                  v-bind="attrs"
                  v-on="on"
                  prepend-inner-icon="mdi-clock-outline"
                ></v-text-field>
              </template>
              <v-time-picker
                v-if="timePickerMenu"
                v-model="selectedAssign.classStartTime"
                full-width
                :min="classTimeMin"
                max="24:00"
                :allowed-minutes="allowedStep"
                :allowed-seconds="[ 0 ]"
                @click:minute="$refs.dialog.save(selectedAssign.classStartTime)"
                color="primary"
              >
                <v-spacer></v-spacer>
                <v-btn text color="primary" @click="timePickerMenu = false">{{ $t('cancel') }}</v-btn>
                <v-btn text color="primary" @click="$refs.dialog.save(selectedAssign.classStartTime)">{{ $t('confirm') }}</v-btn>
              </v-time-picker>
            </v-dialog>
          </v-col>
          <v-col cols="12" md="6">
            <div class="weekday-selector pa-2">
              <v-item-group multiple v-model="selectedAssign.assignDays" class="d-flex flex-wrap">
                <v-item v-for="item in app.weekdays" :key="item.value" :value="item.value" v-slot="{ active, toggle }">
                  <v-btn
                    :id="item.dayOfWeek"
                    :color="active ? 'primary' : ''"
                    :class="{'white--text': active}"
                    :value="(item.value)"
                    @click="toggle"
                    class="mr-1 mb-1 weekday-btn"
                    elevation="1"
                    x-small
                  >
                    {{ item.dayOfWeek }}
                  </v-btn>
                </v-item>
              </v-item-group>
              <div class="caption mt-1 selected-days">{{ $t('currently_selected_days') }}: <strong class="primary--text">{{ selectedDays }}</strong></div>
              <div class="caption text-grey">{{ $t('class_example') }}</div>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- 수업 주제 섹션 -->
    <v-card class="section-card mt-2" outlined>
      <v-card-title class="section-title py-2">
        <v-icon left color="primary" small>mdi-book-open-variant</v-icon>
        <span class="title-text">{{ $t('class_topic') }}</span>
      </v-card-title>
      <v-card-text class="pa-2">
        <v-item-group v-model="selectedAssign.contentId" mandatory @change="handleContentChange">
          <v-row dense>
            <v-col v-for="item in contentState.content" :key="item.contentId" cols="6" sm="4" md="3" lg="2">
              <v-item :value="item.contentId" v-slot="{ active, toggle }">
                <v-btn
                  :color="active ? 'primary' : ''"
                  :class="{'white--text': active, 'content-btn': true}"
                  @click="toggle"
                  block
                  height="50"
                  elevation="1"
                  x-small
                  width="100%"
                >                
                  <div class="content-btn-text" style="text-overflow: clip">
                    <template v-if="isManager || isTeacher">
                      {{ item.contentName }}
                    </template>
                    <template v-else>
                      {{ item.description }}
                    </template>
                  </div>
                </v-btn>
              </v-item>
            </v-col>
          </v-row>
        </v-item-group>
      </v-card-text>
    </v-card>

    <!-- 수업 방식 및 수강권 선택 섹션 -->
    <v-row dense class="mt-2">
      <v-col cols="12" sm="6">
        <v-card class="section-card h-100" outlined>
          <v-card-title class="section-title py-2">
            <v-icon left color="primary" small>mdi-teach</v-icon>
            <span class="title-text">{{ $t('class_method') }}</span>
          </v-card-title>
          <v-card-text class="pa-2">
            <v-item-group v-model="selectedAssign.callType" mandatory>
              <v-row dense>
                <v-col cols="6">
                  <v-item v-slot="{ active, toggle }" value="Video(화상 수업)">
                    <v-btn 
                      block 
                      :color="active ? 'primary' : ''" 
                      :class="{'white--text': active, 'call-type-btn': true}"
                      @click="toggle" 
                      height="50"
                      elevation="1"
                      x-small                    
                    >
                      <v-icon left small>mdi-video</v-icon>
                      {{ $t('call_video')}}
                    </v-btn>
                  </v-item>
                </v-col>
                <v-col cols="6">
                  <v-item v-slot="{ active, toggle }" value="Audio(음성 수업)">
                    <v-btn 
                      block 
                      :color="active ? 'primary' : ''" 
                      :class="{'white--text': active, 'call-type-btn': true}"
                      @click="toggle" 
                      height="50"
                      elevation="1"
                      x-small
                    >
                      <v-icon left small>mdi-phone</v-icon>
                      {{ $t('call_audio')}}
                    </v-btn>
                  </v-item>
                </v-col>
              </v-row>
            </v-item-group>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6">
        <v-card class="section-card h-100" outlined>
          <v-card-title class="section-title py-2">
            <v-icon left color="primary" small>mdi-credit-card-outline</v-icon>
            <span class="title-text">{{ $t('select_course') }}</span>
          </v-card-title>
          <v-card-text class="pa-2">
            <v-item-group v-model="selectedAssign.courseId" mandatory>
              <v-row dense>
                <v-col v-for="item in app.course" :key="item.courseId" cols="4">
                  <v-item v-slot="{ active, toggle }">
                    <v-card
                      :color="active ? 'primary' : ''"
                      :class="{ 'white--text': active, 'course-card': true }"
                      @click="toggle"
                      :disabled="calledPageType.toLowerCase().includes('assign') && selectedAssign.courseId !== item.courseId"
                      class="d-flex flex-column align-center justify-center"
                      height="90"
                      outlined
                      hover
                      elevation="1"
                    >
                      <div class="text-subtitle-1 font-weight-medium">{{ item.duration }}{{ $t('minutes') }}</div>
                      <div class="text-caption">{{ item.classCount }}{{ $t('times') }}</div>
                      <div v-if="!isTeacher" class="text-subtitle-2 mt-1 font-weight-bold">￦{{ item.price.toLocaleString() }}</div>
                    </v-card>
                  </v-item>
                </v-col>
              </v-row>
            </v-item-group>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <div class="mt-2">
      <slot name="action"></slot>
    </div>
  </v-card>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import device_util from "@/utils/util_device";

export default {
  name: 'CardClass',
  components: {},
  props: {
    selectedAssign: {type: Object},
    user: {type: Object},
    calledPageType: {type: String, default: ''}
  },
  data() {
    return {
      datePickerMenu: false,
      timePickerMenu: false,
      listLoading: true,
      skypeIdRule: [v => !!v || 'Teams 혹은 Zoom 계정 입력해주세요.'],
      contentIdWatcher: null
    }
  },
  computed: {
    ...mapGetters(['app', 'contentState', 'isTeacher','isOwner', 'isManager']), 
    classTimeMin() { return this.isManager ? '6:00' : '8:00'},
    formattedTime() {
      let classStartTime = this.$parseStringToTime(this.selectedAssign.classStartTime)
      return classStartTime.toLocaleString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true })
    },
    isAssign(){
      return this.selectedAssign.assignId != null
    },
    selectedCourseDescription: function (){
      let course = this.app.course.filter(c => c.courseId === this.selectedAssign.courseId)[0]
      return course ? course.description : ''
    },
    selectedCoursePrice: function (){
      let course = this.app.course.filter(c => c.courseId === this.selectedAssign.courseId)[0]
      return course ? course.price.toLocaleString() : ''
    },
    selectedDays: function (){
      if (this.selectedAssign.assignDays === null) return ''

      // Create a copy to avoid mutating the original array
      const assignDaysCopy = [...this.selectedAssign.assignDays].filter(day => day !== 0)
      
      return assignDaysCopy.sort().map(day => {
        const dayObject = this.app.weekdays.find(d => d.value === day)
        return dayObject ? dayObject.dayOfWeek : ''
      }).filter(Boolean).join(', ')
    },
    studentEnglishName: function (){
      if (this.selectedAssign.registerId || this.selectedAssign.assignId) {
        return this.selectedAssign.studentEngName
      } else {
        return this.user.username
      }
    },
    studentEmail: function (){
      if (this.selectedAssign.registerId || this.selectedAssign.assignId) {
        return this.selectedAssign.email
      } else {
        return this.user.email
      }
    },
    teacherName: function (){
      return this.selectedAssign.teacher ? this.selectedAssign.teacher.username : ''
    },
    skypeDownloadLink: function () {
      let mobileDeviceType = device_util.getMobileDeviceType
      switch (mobileDeviceType){
        case 'ios':          return 'https://www.microsoft.com/ko-kr/microsoft-teams/download-app'
        case 'android':      return 'https://www.microsoft.com/ko-kr/microsoft-teams/download-app'
        default:             return 'https://www.microsoft.com/ko-kr/microsoft-teams/download-app'
      }
    },
    skypeInstallGuideLink: function() {
      return process.env.VUE_APP_WEB + '/main/guide/call'
    },
  },
  watch: {
    'contentState.content': {
      handler(newContent) {
        if (newContent && newContent.length > 0) {
          const selectedContent = newContent.find(item => item.contentId === this.selectedAssign.contentId);
          if (!selectedContent) {
            this.selectedAssign.contentId = newContent[0].contentId;
          }
        }
      },
      immediate: true
    }
  },
  created() { 
    if (!this.selectedAssign.startDate) {
      this.selectedAssign.startDate = this.$formattedCurrentDate();
    }
  },
  methods:{
    ...mapActions(['updateUser']),
    allowedStep: m => m % 10 === 0,
    isDisabledCourse(item) {
      if (this.selectedAssign.assignId) {
        return this.selectedAssign.courseId !== item.courseId
      } else {
        return false
      }
    },
    selectApp(app){
      if(app.appCode === 'Teams' && !this.selectedAssign.skypeId){
        this.selectedAssign.skypeId = ''
      }
      if (app.appCode === 'Zoom' && !this.selectedAssign.zoomId){
        if (this.user.accountSnsList && this.user.accountSnsList.filter(as => as.providerType === 'GOOGLE').length > 0)
          this.selectedAssign.skypeId = this.user.accountSnsList.filter(as => as.providerType === 'GOOGLE')[0].snsEmail
      }
    },
    validate(){
      this.selectedAssign.course = this.app.course.filter(c => c.courseId === this.selectedAssign.courseId)[0]
      this.selectedAssign.content = this.contentState.content.filter(c => c.contentId === this.selectedAssign.contentId)[0]
      
      // Create a copy to avoid mutating the original array
      if (this.selectedAssign.assignDays) {
        this.selectedAssign.assignDays = [...this.selectedAssign.assignDays].filter(v => v != 0)
      }
      
      this.selectedAssign.student = this.user

      let valid = true
      let message = ''

      if (!this.selectedAssign.skypeId || !this.selectedAssign.skypeId.trim()) {
        this.$refs.skypeId.$el.scrollIntoView({behavior:"smooth"})
        message = 'Teams Id'
        valid = false
      }
      if (!valid) {
        this.$dialog.notify.error(this.$t('please_check_item', { item: message }), {position: 'top-right', timeout: 1000})
      }
      return valid
    },
    saveEnglishName(){
      this.updateUser(this.selectedAssign.student)
          .then(() => this.$dialog.notify.info('Success', {position: 'top-right', timeout: 1000}))
          .catch(err => this.$dialog.notify.error(`${err.message}`, {position: 'top-right', timeout: 1000}))
    },
    openWindow(link, title){
      this.$openWindow(link, title)
    },
    callAppImg(callAppType) {
      if (callAppType === 'Teams') return `${this.app.cdnUrl}/website/button/btn_teams.png`
      if (callAppType === 'Zoom') return `${this.app.cdnUrl}/website/button/btn_zoom.png`
      return ''
    },
    handleContentChange(contentId) {
      this.selectedAssign.contentId = contentId;
    },
  }
}
</script>

<style lang="scss">
@import "@/assets/scss/elegant-theme.scss";

.card-class-container {
  border-radius: 8px;
}

.section-card {
  border-radius: 6px;
  transition: all 0.2s ease;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05) !important;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08) !important;
  }
}

.section-title {
  font-size: 0.95rem !important;
  font-weight: 600 !important;
  padding: 8px 12px !important;
  background-color: rgba(var(--v-primary-base), 0.05);
  border-bottom: 1px solid rgba(var(--v-primary-base), 0.1);
  
  .title-text {
    font-size: 0.95rem;
    line-height: 1.2;
  }
}

.app-select-btn, .weekday-btn, .content-btn, .call-type-btn {
  transition: all 0.2s ease;
  border-radius: 6px !important;
  text-transform: none;
  letter-spacing: 0.5px;
  font-size: 0.8rem !important;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.08) !important;
  }
}

.content-btn-text {
  width: 100%;
  white-space: normal;
  word-break: break-word;
  word-wrap: normal;
  font-size: 0.75rem;
  line-height: 1.2;
}

.weekday-selector {
  padding: 6px;
  border-radius: 6px;
  background-color: rgba(0, 0, 0, 0.02);
}

.selected-days {
  font-size: 0.8rem;
}

.course-card {
  border-radius: 6px !important;
  transition: all 0.2s ease;
  
  &:hover:not([disabled]) {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.08) !important;
  }
  
  &[disabled] {
    opacity: 0.6;
  }
}

.h-100 {
  height: 100%;
}

.rounded-lg {
  border-radius: 6px !important;
}

// Override Vuetify default styles
.v-text-field--outlined >>> fieldset {
  border-radius: 6px;
}

.v-btn {
  letter-spacing: 0.5px;
}

// .v-btn__content {
//   width: 100%;
// } 

// Mobile optimizations
@media (max-width: 600px) {
  .section-title {
    font-size: 0.85rem !important;
    padding: 6px 10px !important;
    
    .title-text {
      font-size: 0.85rem;
    }
  }
  
  .content-btn-text {
    font-size: 0.7rem;
  }
  
  .selected-days {
    font-size: 0.75rem;
  }
  
  .weekday-btn {
    margin-right: 2px !important;
    margin-bottom: 2px !important;
    min-width: 32px !important;
    width: auto !important;
    padding: 0 4px !important;
  }
  
  .v-card__text {
    padding: 8px 6px !important;
  }
  
  .course-card {
    height: 80px !important;
  }
}
</style>